import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useTranslation } from 'react-i18next';
import ChartPlaceholder from './ChartPlaceholder';
import { resolvePointColor } from './chartConfigUtils';

interface ExpiredGoldPctPoint {
    readonly month: string;
    readonly avgActivePassiveEur: number;
    readonly avgActivePassiveLei: number;
    readonly expiredEur: number;
    readonly expiredLei: number;
    readonly pctEur: number;
    readonly pctLei: number;
}

interface ExpiredGoldPctChartProps {
    readonly data: readonly ExpiredGoldPctPoint[];
    readonly loading?: boolean;
    readonly currency?: 'EUR' | 'LEI';
}

const localeAxisFormatter: Highcharts.AxisLabelsFormatterCallbackFunction = function () {
    return this.value?.toLocaleString() ?? '';
};

const expiredGoldPointFormatter: Highcharts.FormatterCallbackFunction<Highcharts.Point> =
    function () {
        const color = resolvePointColor(this.color, this.series?.color);
        const isPercentage = this.series.yAxis.options.opposite;
        const formatted = isPercentage
            ? `${(this.y ?? 0).toFixed(2)}%`
            : Math.round(this.y ?? 0).toLocaleString();
        return `<span style="color:${color}">●</span> ${this.series.name}: <b>${formatted}</b><br/>`;
    };

function ExpiredGoldPctChart({
    data,
    loading = false,
    currency,
}: ExpiredGoldPctChartProps): JSX.Element {
    const { t } = useTranslation();

    if (loading || data.length === 0) {
        return <ChartPlaceholder loading={loading} hasData={data.length > 0} />;
    }

    const isEur = currency === 'EUR';

    const options: Highcharts.Options = {
        chart: {
            height: 340,
            spacing: [8, 8, 8, 8],
            style: { fontFamily: 'inherit' },
        },
        title: { text: undefined },
        xAxis: {
            categories: data.map(d => d.month),
            labels: { style: { fontSize: '11px', color: '#888' } },
        },
        yAxis: [
            {
                title: {
                    text: t('statistics.value.expiredValueAxis', 'Expired Value'),
                    style: { fontSize: '12px', color: '#888' },
                },
                labels: {
                    style: { fontSize: '11px' },
                    formatter: localeAxisFormatter,
                },
            },
            {
                title: {
                    text: t('statistics.value.percentageAxis', '% of Active+Passive'),
                    style: { fontSize: '12px', color: '#888' },
                },
                labels: {
                    style: { fontSize: '11px' },
                    format: '{value}%',
                },
                opposite: true,
            },
        ],
        series: [
            {
                name: isEur
                    ? t('statistics.value.expiredEur', 'Expired (EUR)')
                    : t('statistics.value.expiredLei', 'Expired (RON)'),
                type: 'column',
                yAxis: 0,
                data: data.map(d => Number(isEur ? (d.expiredEur ?? 0) : (d.expiredLei ?? 0))),
                color: isEur ? '#faad14' : '#ff7a45',
            },
            {
                name: isEur
                    ? t('statistics.value.pctEur', '% (EUR)')
                    : t('statistics.value.pctLei', '% (RON)'),
                type: 'spline',
                yAxis: 1,
                data: data.map(d => Number(isEur ? (d.pctEur ?? 0) : (d.pctLei ?? 0))),
                color: isEur ? '#cf1322' : '#722ed1',
                marker: { enabled: true, radius: 4 },
                lineWidth: 2,
            },
        ],
        legend: { enabled: true },
        credits: { enabled: false },
        tooltip: {
            shared: true,
            useHTML: true,
            backgroundColor: '#fff',
            borderColor: '#ccc',
            borderRadius: 4,
            padding: 8,
            pointFormatter: expiredGoldPointFormatter,
        },
    };

    return <HighchartsReact highcharts={Highcharts} options={options} />;
}

export default ExpiredGoldPctChart;
